
import React from 'react';
import { useNavigate } from 'react-router-dom';
import Header from '../../../components/feature/Header';
import Footer from '../../../components/feature/Footer';
import Button from '../../../components/base/Button';

export default function HealthInsurance() {
  const navigate = useNavigate();

  const benefits = [
    {
      icon: 'ri-calendar-event-line',
      title: 'Open Enrollment Campaigns',
      description: 'Strategic campaigns timed for Medicare and ACA open enrollment periods to maximize lead volume.'
    },
    {
      icon: 'ri-search-line',
      title: 'Provider Network Tools',
      description: 'Interactive tools helping prospects find doctors and hospitals in their preferred insurance networks.'
    },
    {
      icon: 'ri-calculator-line',
      title: 'Subsidy Calculators',
      description: 'Advanced calculators that help prospects understand their potential ACA subsidies and savings.'
    },
    {
      icon: 'ri-shield-check-line',
      title: 'Plan Comparison Tools',
      description: 'Side-by-side plan comparison features that help prospects choose the right coverage level.'
    }
  ];

  const process = [
    {
      step: '01',
      title: 'Demographic Analysis',
      description: 'Identify target demographics based on age, income, health status, and insurance needs.'
    },
    {
      step: '02',
      title: 'Compliance Setup',
      description: 'Ensure all campaigns meet CMS guidelines and state insurance advertising regulations.'
    },
    {
      step: '03',
      title: 'Educational Campaigns',
      description: 'Create informative campaigns that educate prospects about insurance options and benefits.'
    },
    {
      step: '04',
      title: 'Enrollment Optimization',
      description: 'Optimize for enrollment conversion with clear calls-to-action and enrollment assistance.'
    }
  ];

  const stats = [
    { number: '650K+', label: 'Health Insurance Leads' },
    { number: '19%', label: 'Average Conversion Rate' },
    { number: '$195', label: 'Average Cost Per Lead' },
    { number: '76%', label: 'Enrollment Rate' }
  ];

  const insuranceTypes = [
    {
      icon: 'ri-heart-pulse-line',
      title: 'Medicare Plans',
      description: 'Specialized targeting for Medicare Advantage, Supplement, and Part D prescription drug plans.',
      features: ['Medicare Advantage', 'Supplement Insurance', 'Part D Drug Plans', 'Medicare Education']
    },
    {
      icon: 'ri-shield-user-line',
      title: 'ACA Marketplace',
      description: 'Generate leads for individual and family health insurance plans through state and federal marketplaces.',
      features: ['Individual Plans', 'Family Coverage', 'Subsidy Qualification', 'Plan Comparison']
    },
    {
      icon: 'ri-briefcase-line',
      title: 'Group Insurance',
      description: 'B2B lead generation for employers seeking group health insurance coverage for their employees.',
      features: ['Small Business Plans', 'Employee Benefits', 'Group Rates', 'Employer Solutions']
    }
  ];

  return (
    <div className="min-h-screen bg-white">
      <Header />
      
      {/* Hero Section */}
      <section 
        className="relative bg-cover bg-center bg-no-repeat py-32"
        style={{
          backgroundImage: `linear-gradient(rgba(249, 115, 22, 0.8), rgba(234, 88, 12, 0.8)), url('https://readdy.ai/api/search-image?query=Healthcare%20insurance%20specialist%20helping%20family%20review%20health%20coverage%20options%20on%20computer%20screen%20in%20bright%20medical%20office%2C%20insurance%20documents%20and%20comparison%20charts%20visible%2C%20professional%20healthcare%20consultation%20environment&width=1920&height=800&seq=health-insurance-hero&orientation=landscape')`
        }}
      >
        <div className="max-w-7xl mx-auto px-6">
          <div className="max-w-4xl">
            <h1 className="text-5xl md:text-6xl font-bold text-white mb-6">
              Health Insurance Lead Generation
            </h1>
            <p className="text-xl md:text-2xl text-orange-100 mb-8">
              Connect health insurance providers with qualified prospects seeking comprehensive coverage, Medicare plans, and competitive rates through targeted digital marketing campaigns.
            </p>
            <div className="flex flex-col sm:flex-row gap-4">
              <Button 
                size="lg"
                onClick={() => navigate('/contact')}
                className="bg-white text-black hover:bg-gray-100 font-semibold"
              >
                <i className="ri-heart-line mr-2 text-black"></i>
                Start Your Campaign
              </Button>
              <Button 
                variant="outline"
                size="lg"
                onClick={() => navigate('/case-studies')}
                className="border-white text-white hover:bg-white hover:text-orange-600 font-semibold"
              >
                <i className="ri-bar-chart-line mr-2"></i>
                View Success Stories
              </Button>
            </div>
          </div>
        </div>
      </section>

      {/* Stats Section */}
      <section className="py-20 bg-gray-50">
        <div className="max-w-7xl mx-auto px-6">
          <div className="text-center mb-16">
            <h2 className="text-4xl md:text-5xl font-bold text-gray-900 mb-6">
              Health Insurance Lead Results
            </h2>
            <p className="text-xl text-gray-600 max-w-3xl mx-auto">
              Our health insurance campaigns deliver qualified prospects ready to enroll in coverage.
            </p>
          </div>

          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-8">
            {stats.map((stat, index) => (
              <div key={index} className="bg-white p-8 rounded-2xl shadow-lg text-center">
                <div className="text-4xl font-bold text-orange-600 mb-2">{stat.number}</div>
                <div className="text-gray-600 font-medium">{stat.label}</div>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* Insurance Types Section */}
      <section className="py-20 bg-white">
        <div className="max-w-7xl mx-auto px-6">
          <div className="text-center mb-16">
            <h2 className="text-4xl md:text-5xl font-bold text-gray-900 mb-6">
              Comprehensive Insurance Coverage
            </h2>
            <p className="text-xl text-gray-600 max-w-3xl mx-auto">
              We generate leads across all major health insurance categories and enrollment periods.
            </p>
          </div>

          <div className="grid grid-cols-1 lg:grid-cols-3 gap-8">
            {insuranceTypes.map((type, index) => (
              <div key={index} className="bg-white p-8 rounded-2xl shadow-lg hover:shadow-xl transition-shadow border border-gray-100">
                <div className="w-16 h-16 flex items-center justify-center bg-orange-100 rounded-2xl mb-6">
                  <i className={`${type.icon} text-2xl text-orange-600`}></i>
                </div>
                <h3 className="text-2xl font-bold text-gray-900 mb-4">{type.title}</h3>
                <p className="text-gray-600 mb-6 leading-relaxed">{type.description}</p>
                <ul className="space-y-3">
                  {type.features.map((feature, featureIndex) => (
                    <li key={featureIndex} className="flex items-center">
                      <div className="w-2 h-2 bg-orange-600 rounded-full mr-3"></div>
                      <span className="text-gray-700">{feature}</span>
                    </li>
                  ))}
                </ul>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* Benefits Section */}
      <section className="py-20 bg-orange-50">
        <div className="max-w-7xl mx-auto px-6">
          <div className="text-center mb-16">
            <h2 className="text-4xl md:text-5xl font-bold text-gray-900 mb-6">
              Specialized Health Insurance Marketing
            </h2>
            <p className="text-xl text-gray-600 max-w-3xl mx-auto">
              Our campaigns are designed to educate prospects and guide them through the complex world of health insurance.
            </p>
          </div>

          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-8">
            {benefits.map((benefit, index) => (
              <div key={index} className="bg-white p-8 rounded-2xl shadow-lg text-center">
                <div className="w-20 h-20 flex items-center justify-center bg-orange-100 rounded-full mx-auto mb-6">
                  <i className={`${benefit.icon} text-3xl text-orange-600`}></i>
                </div>
                <h3 className="text-xl font-bold text-gray-900 mb-4">{benefit.title}</h3>
                <p className="text-gray-600 leading-relaxed">{benefit.description}</p>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* Process Section */}
      <section className="py-20 bg-white">
        <div className="max-w-7xl mx-auto px-6">
          <div className="text-center mb-16">
            <h2 className="text-4xl md:text-5xl font-bold text-gray-900 mb-6">
              Our Health Insurance Lead Process
            </h2>
            <p className="text-xl text-gray-600 max-w-3xl mx-auto">
              A compliant and educational approach that builds trust while generating high-quality insurance leads.
            </p>
          </div>

          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-8">
            {process.map((step, index) => (
              <div key={index} className="bg-gray-50 p-8 rounded-2xl shadow-lg text-center">
                <div className="w-16 h-16 flex items-center justify-center bg-orange-600 text-white text-2xl font-bold rounded-full mx-auto mb-6">
                  {step.step}
                </div>
                <h3 className="text-xl font-bold text-gray-900 mb-4">{step.title}</h3>
                <p className="text-gray-600 leading-relaxed">{step.description}</p>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* Features Section */}
      <section className="py-20 bg-gray-50">
        <div className="max-w-7xl mx-auto px-6">
          <div className="flex flex-col lg:flex-row items-center gap-12">
            <div className="lg:w-1/2">
              <img 
                src="https://readdy.ai/api/search-image?query=Professional%20insurance%20agent%20explaining%20Medicare%20and%20health%20insurance%20options%20to%20elderly%20couple%20using%20laptop%20and%20brochures%20in%20comfortable%20office%20setting%2C%20showing%20plan%20comparison%20charts%20and%20enrollment%20forms&width=600&height=400&seq=insurance-consultation&orientation=landscape"
                alt="Insurance Consultation"
                className="w-full rounded-2xl shadow-lg object-cover object-top"
              />
            </div>
            <div className="lg:w-1/2">
              <h2 className="text-4xl md:text-5xl font-bold text-gray-900 mb-6">
                Compliant Insurance Marketing
              </h2>
              <p className="text-lg text-gray-600 mb-8 leading-relaxed">
                All our health insurance campaigns comply with CMS guidelines and state regulations while providing valuable education to help prospects make informed decisions.
              </p>
              <div className="space-y-4">
                <div className="flex items-center">
                  <div className="w-8 h-8 flex items-center justify-center bg-orange-100 rounded-lg mr-4">
                    <i className="ri-check-line text-orange-600"></i>
                  </div>
                  <span className="text-gray-700">CMS compliant advertising and messaging</span>
                </div>
                <div className="flex items-center">
                  <div className="w-8 h-8 flex items-center justify-center bg-orange-100 rounded-lg mr-4">
                    <i className="ri-check-line text-orange-600"></i>
                  </div>
                  <span className="text-gray-700">Medicare and ACA enrollment expertise</span>
                </div>
                <div className="flex items-center">
                  <div className="w-8 h-8 flex items-center justify-center bg-orange-100 rounded-lg mr-4">
                    <i className="ri-check-line text-orange-600"></i>
                  </div>
                  <span className="text-gray-700">Educational content and plan comparisons</span>
                </div>
                <div className="flex items-center">
                  <div className="w-8 h-8 flex items-center justify-center bg-orange-100 rounded-lg mr-4">
                    <i className="ri-check-line text-orange-600"></i>
                  </div>
                  <span className="text-gray-700">Real-time enrollment support and tracking</span>
                </div>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* CTA Section */}
      <section className="py-20 bg-orange-600">
        <div className="max-w-4xl mx-auto px-6 text-center">
          <h2 className="text-4xl md:text-5xl font-bold text-white mb-6">
            Connect More People with Health Coverage
          </h2>
          <p className="text-xl text-orange-100 mb-8">
            Help us expand access to quality health insurance by partnering with our expert lead generation team.
          </p>
          <div className="flex flex-col sm:flex-row gap-4 justify-center">
            <a 
              href="tel:+910120418794"
              className="inline-flex items-center justify-center px-6 py-3 bg-white text-black hover:bg-gray-100 rounded-lg font-semibold transition-colors whitespace-nowrap cursor-pointer"
            >
              <i className="ri-phone-line mr-2"></i>
              Call +91 0120 418 7944
            </a>
            <Button 
              variant="outline"
              size="lg"
              onClick={() => navigate('/services')}
              className="border-white text-white hover:bg-white hover:text-orange-600"
            >
              <i className="ri-arrow-left-line mr-2"></i>
              All Services
            </Button>
          </div>
        </div>
      </section>

      <Footer />
    </div>
  );
}
